import { useState } from "react";

const faqs = [
  {
    category: "Product Sourcing",
    icon: "ri-search-eye-line",
    pillarColor: "text-[#C9A227]",
    pillarBg: "bg-[#C9A227]/10",
    questions: [
      {
        q: "How long does it take to source a product?",
        a: "After you submit your brief, our team works to identify verified supplier options within a few business days. Custom or highly specialized products may take longer — typically 5–10 business days for matching, with sample production at 10–20 days and bulk production at 30–60 days from approval depending on the supplier.",
        link: { text: "See the full Product Sourcing process", href: "#how-it-works" },
      },
      {
        q: "Is there a minimum order quantity for sourcing requests?",
        a: "No hard minimum — our Product Sourcing pillar works with businesses of all sizes. Smaller quantities may carry slightly higher per-unit costs, but our team always presents the most cost-effective options and flags the volume thresholds that unlock better pricing. This is handled during Step 3: Quote Comparison.",
        link: { text: "How Quote Comparison works", href: "#how-it-works" },
      },
      {
        q: "Can you handle rush or urgent sourcing needs?",
        a: "We'll always do our best to accommodate time-sensitive requests. Reach out with your deadline and we'll be upfront about what's realistically achievable — we won't rush a supplier match at the expense of quality or proper vetting.",
        link: null,
      },
    ],
  },
  {
    category: "Wholesale Supply",
    icon: "ri-store-3-line",
    pillarColor: "text-[#3A506B]",
    pillarBg: "bg-[#D9E2EC]",
    questions: [
      {
        q: "How are wholesale suppliers verified before I'm connected to them?",
        a: "Every supplier in our Wholesale Supply network goes through a vetting process before we make an introduction. This includes business license verification, background checks, quality system review, and ongoing performance monitoring. We only connect you with suppliers we've already approved.",
        link: { text: "See the Wholesale Supply process", href: "#how-it-works" },
      },
      {
        q: "What product categories are available through wholesale?",
        a: "Our Wholesale Supply pillar currently covers 40+ product categories — including consumer goods, electronics, apparel, industrial supplies, and more. We're actively expanding our network. Submit a custom bulk request and our team will let you know if we can match your specific needs.",
        link: null,
      },
      {
        q: "What happens if a wholesale supplier delivers defective goods?",
        a: "Our process includes a Pre-Shipment QC step designed to catch issues before anything ships. If problems do arise after delivery, LTF Sourcing will act as your advocate — helping manage supplier disputes and working toward a resolution, whether that's a replacement, credit, or refund.",
        link: { text: "How Pre-Shipment QC works", href: "#how-it-works" },
      },
    ],
  },
  {
    category: "Inventory Solutions",
    icon: "ri-archive-drawer-line",
    pillarColor: "text-[#C9A227]",
    pillarBg: "bg-[#C9A227]/10",
    questions: [
      {
        q: "How does listing my overstock inventory work?",
        a: "Start by submitting your inventory details — product type, condition, quantity, and target price. Our team then assesses market demand and works to connect you with relevant bulk buyers from our network. The process is straightforward and we'll keep you updated at each step.",
        link: { text: "See the full Inventory Solutions process", href: "#how-it-works" },
      },
      {
        q: "How quickly can overstock inventory be cleared?",
        a: "Timelines vary depending on product category, condition, and current buyer demand. Some listings move within a couple of weeks; others take longer. We'll give you an honest assessment after reviewing your inventory — we'd rather set realistic expectations than overpromise.",
        link: { text: "How Buyer Matching works", href: "#how-it-works" },
      },
      {
        q: "What condition is overstock merchandise typically in?",
        a: "Most inventory available through the Inventory Solutions pillar is brand new — surplus from overproduction, canceled orders, or seasonal changeovers. Open-box, refurbished, or cosmetically blemished items are clearly labeled. All condition details are fully disclosed before any purchase commitment is made.",
        link: null,
      },
    ],
  },
  {
    category: "Logistics & Delivery",
    icon: "ri-truck-line",
    pillarColor: "text-teal-600",
    pillarBg: "bg-teal-50",
    questions: [
      {
        q: "Does LTF Sourcing handle international shipping and customs?",
        a: "Yes — logistics support is included in both the Product Sourcing and Wholesale Supply pillars. We coordinate freight options, help with customs documentation, and work with brokers to keep things moving. For Inventory Solutions, we also assist with buyer pickup or outbound delivery as part of the closing step.",
        link: { text: "See how delivery works across all three pillars", href: "#how-it-works" },
      },
      {
        q: "Which countries does LTF Sourcing serve?",
        a: "We currently have active operations across 8+ countries and are expanding our reach. Reach out with your location and we'll confirm whether we can support your needs and flag any relevant import or export considerations before you commit to anything.",
        link: null,
      },
    ],
  },
];

export default function FAQSection() {
  const [openItem, setOpenItem] = useState<string | null>("0-0");

  const toggle = (key: string) => {
    setOpenItem(openItem === key ? null : key);
  };

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="flex flex-col items-center text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#C9A227] mb-3">
            FAQ
          </span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#222831] leading-tight">
            Questions About Every Pillar
          </h2>
          <p className="mt-4 text-base text-[#3A506B] max-w-xl leading-relaxed">
            Everything you need to know about Product Sourcing, Wholesale Supply, and
            Inventory Solutions — plus logistics and delivery across all three.
          </p>
        </div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {faqs.map((category, catIdx) => (
            <div key={category.category} className="flex flex-col gap-3">

              {/* Category Label */}
              <div className="flex items-center gap-3 mb-1">
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${category.pillarBg}`}>
                  <i className={`${category.icon} ${category.pillarColor} text-base`}></i>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#3A506B]/60">Pillar</p>
                  <h3 className={`text-sm font-bold uppercase tracking-wide ${category.pillarColor}`}>
                    {category.category}
                  </h3>
                </div>
              </div>

              {/* Questions */}
              {category.questions.map((item, qIdx) => {
                const key = `${catIdx}-${qIdx}`;
                const isOpen = openItem === key;
                return (
                  <div
                    key={key}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? "border-[#C9A227] bg-[#C9A227]/5"
                        : "border-[#D9E2EC] bg-white hover:border-[#C9A227]/40"
                    }`}
                  >
                    <button
                      onClick={() => toggle(key)}
                      className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    >
                      <span
                        className={`text-sm font-semibold leading-snug transition-colors ${
                          isOpen ? "text-[#C9A227]" : "text-[#222831]"
                        }`}
                      >
                        {item.q}
                      </span>
                      <div
                        className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all duration-200 ${
                          isOpen
                            ? "bg-[#C9A227] text-white rotate-45"
                            : "bg-[#D9E2EC] text-[#3A506B]"
                        }`}
                      >
                        <i className="ri-add-line text-sm"></i>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-5">
                        <div className="h-px bg-[#C9A227]/20 mb-4"></div>
                        <p className="text-sm text-[#3A506B] leading-relaxed">{item.a}</p>
                        {item.link && (
                          <button
                            onClick={() => scrollTo(item.link!.href)}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#C9A227] hover:gap-2.5 transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-arrow-right-circle-line text-sm"></i>
                            {item.link.text}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 rounded-2xl bg-[#222831] p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-xl">Still have questions?</p>
            <p className="text-white/60 text-sm mt-1">
              Our procurement specialists are available Mon–Fri, 9AM–6PM EST.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="mailto:info@ltfsourcing.com"
              rel="nofollow"
              className="px-6 py-3 rounded-full border-2 border-white/30 text-white text-sm font-semibold hover:border-white transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-mail-line text-base"></i>
              Email Us
            </a>
            <button
              onClick={() => scrollTo("#contact")}
              className="px-6 py-3 rounded-full bg-[#C9A227] text-white text-sm font-bold hover:bg-[#b08f1e] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-chat-1-line text-base"></i>
              Talk to a Specialist
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
